import { Auth } from "aws-amplify";
import { useContext, useEffect } from "react";
import { UserType } from "../../../graphql/types";
import { UserTypeContext } from "../../../UserTypeContext";
import enumStrings, { RedirectUri } from "../../enumStrings";
import { getAdminConsoleUserTypeFromCognitoGroups } from "../utilities/adminConsoleUserType";
import { hasPendingNewPasswordChallenge } from "../../pages/Login/pendingNewPasswordChallenge";

const useInitialAuth = (setIsLoggedIn: (val: any) => void) => {
  const {
    setters: { setUserType },
  } = useContext(UserTypeContext);

  return useEffect(() => {
    const checkAccessToken = async (): Promise<void> => {
      // Remove plaintext credentials left by any legacy build regardless of
      // whether the current access token is still valid. New password
      // challenges are held only in memory.
      sessionStorage.removeItem(
        enumStrings.sessionStorageKey.loggedInWithTemporaryPassword
      );

      let accessTokenIsValid = false;
      let userType: UserType | "" = "";
      try {
        const res = await Auth.currentSession();
        const authenticatedUser = await Auth.currentAuthenticatedUser();
        userType = getAdminConsoleUserTypeFromCognitoGroups(
          res.getAccessToken().decodePayload()["cognito:groups"]
        );
        if (authenticatedUser && userType) {
          accessTokenIsValid = true;
        }
      } catch (error) {
        if (error !== "No current user") {
          throw error;
        }
      }

      // normalize to match react-router's route matching (case-insensitive, optional trailing slash)
      const urlPath = window.location.pathname
        .toLowerCase()
        .replace(/(.+)\/$/, "$1");
      if (!accessTokenIsValid) {
        setIsLoggedIn(false);
        setUserType("");
        const newPasswordChallengeIsPending =
          hasPendingNewPasswordChallenge();

        if (newPasswordChallengeIsPending) {
          if (urlPath !== RedirectUri.newPassword) {
            window.location.href = RedirectUri.newPassword;
          }
          return;
        }

        await Auth.signOut();

        // user isn't logged in and isn't trying to access the cpa delete account page or isn't setting their permanent password... so force them to the login page
        if (
          urlPath !== RedirectUri.cpaPrivacyPolicy &&
          urlPath !== RedirectUri.deleteCpaData &&
          urlPath !== RedirectUri.appStoreSupport &&
          urlPath !== RedirectUri.login &&
          urlPath !== RedirectUri.memberLogin &&
          !newPasswordChallengeIsPending
        ) {
          window.location.href = RedirectUri.login;
        }
      } else {
        setUserType(userType);
        setIsLoggedIn(true);
        localStorage.setItem("accessToken", `${accessTokenIsValid}`);

        if (
          urlPath === RedirectUri.login ||
          urlPath === RedirectUri.memberLogin
        ) {
          window.location.href = RedirectUri.home;
          return;
        }
      }
    };
    checkAccessToken();
  }, [setIsLoggedIn, setUserType]);
};

export default useInitialAuth;
